import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { ThemeColor } from '@vsolv/vectors-ui/theming';
import { CLAIM_LOCK_CONFIG, ClaimLockNotice } from '../../config/claim-lock.config';

interface Section {
  key: 'locks' | 'editable' | 'undo' | 'verify';
  label: string;
  icon: string;
  /** Shown on the collapsed header: a count, or the short undo answer. */
  badge: string;
  theme: ThemeColor;
  items: string[];
  open: boolean;
}

/** Warns what an action locks, in sections the user expands: locked, still editable, undo, and what to check. */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-lock-notice',
  templateUrl: './claim-lock-notice.component.html',
  standalone: false,
})
export class ClaimLockNoticeComponent {
  @Input({ required: true }) set notice(notice: ClaimLockNotice) {
    this.current = notice;
    const labels = CLAIM_LOCK_CONFIG.labels;

    // Built once per notice so a section stays open or closed across change detection.
    this.sections = [
      {
        key: 'locks',
        label: labels.locks,
        icon: 'lock-01',
        badge: `${notice.locks.length}`,
        theme: 'warn',
        items: notice.locks,
        // What gets locked is the point of the warning, so it starts open.
        open: true,
      },
      {
        key: 'editable',
        label: labels.editable,
        icon: 'edit-05',
        badge: `${notice.editable.length}`,
        theme: 'default',
        items: notice.editable,
        open: false,
      },
      {
        key: 'undo',
        label: labels.undo,
        icon: 'reverse-left',
        badge: notice.undoSummary,
        theme: notice.reversible ? 'warn' : 'danger',
        items: [notice.undo],
        open: false,
      },
      {
        key: 'verify',
        label: labels.verify,
        icon: 'check-circle',
        badge: `${notice.verify.length}`,
        theme: 'primary',
        items: notice.verify,
        open: false,
      },
    ].filter(section => section.items.length) as Section[];
  }

  current!: ClaimLockNotice;
  sections: Section[] = [];
}
